import chroma from 'chroma-js';
import {
  ColorMapEntry, Style, RasterSymbolizer, ColorMap,
} from 'geostyler-style';
import { PlaneObservation } from 'services/adsb/dto';
import { ADSBPositions } from 'contexts/ADSBContext/models';
import dayjs, { Dayjs } from 'dayjs';
import { createLogger } from 'services/debugLogger';
import { ColorStep } from '../types/app.types';
import { RangeValue } from '../contexts/TimeSeriesContext';

const logger = createLogger('utils');

export const parseFromGeoStyleToColorSteps = (style?: Style) => {
  const colorSteps: ColorStep[] = [];
  const geoStyle = style;
  if (geoStyle) {
    geoStyle.rules.forEach((rule) => {
      rule.symbolizers.forEach((symbolizer) => {
        if (symbolizer.kind === 'Raster' && symbolizer.colorMap?.colorMapEntries) {
          symbolizer.colorMap.colorMapEntries?.forEach((entry) => {
            const color = chroma(entry.color as string)
              .alpha(entry.opacity !== undefined
                ? entry.opacity as number
                : 1);
            colorSteps.push({
              color: color?.rgba() || '#FFFFFF',
              threshold: entry.quantity as number,
            });
          });
        }
      });
    });
  }
  return colorSteps;
};

export const parseFromColorStepsToGeoStyle = (colorSteps: ColorStep[], baseStyle: Style) => {
  const newStyle = JSON.parse(JSON.stringify(baseStyle));
  const newColorScale: ColorMapEntry[] = colorSteps.map((c, index) => ({
    color: chroma(c.color).hex().substring(0, 7),
    opacity: c.color[3],
    quantity: c.threshold,
  }));
  baseStyle.rules.forEach((rule, index) => {
    rule.symbolizers.forEach((symbolizer, i) => {
      if (symbolizer.kind === 'Raster' && symbolizer.colorMap?.colorMapEntries) {
        ((newStyle
          .rules[index]
          .symbolizers[i] as RasterSymbolizer)
          .colorMap as ColorMap)
          .colorMapEntries = [...newColorScale];
      }
    });
  });
  return newStyle;
};

export const getColorByStyleAndValue = (style: Style, value?: number) : string | undefined => {
  if (value) {
    const colorSteps = parseFromGeoStyleToColorSteps(style);
    const normalizedValue = parseInt(value.toFixed(1), 10);
    const maxValue = Math.max(...colorSteps.map((step) => step.threshold));
    const minValue = Math.min(...colorSteps.map((step) => step.threshold));

    const colorScale = chroma
      .scale(colorSteps.map((step) => chroma(step.color)))
      .domain(colorSteps.map((step) => step.threshold));
      // .colors((maxValue - minValue) * 10); // 10 because we expect numbers to one decimal

    if (normalizedValue <= minValue) {
      return colorScale(minValue).hex();
    }

    if (normalizedValue >= maxValue) {
      return colorScale(maxValue).hex();
    }
    return colorScale(normalizedValue).hex();
  }
  return value as undefined;
};

export const utcDayjs = (time?: Dayjs | string | number | Date): Dayjs => dayjs(time).utc();

export const calculateTime = (rangeStr: string) => {
  const now = utcDayjs();
  const strSplit = rangeStr.split('/');
  let initDate = utcDayjs(strSplit[0]);
  let endDate = initDate.add(1, 'day');

  initDate = now.clone().subtract(1, 'day');
  endDate = now;
  return {
    duration: dayjs.duration(strSplit[2]),
    initDate,
    endDate,
  };
};

const calcMaxFramesToPreload = (minutes: number) => {
  if (minutes <= 30) return 60;
  if (minutes > 30 && minutes <= 60) return minutes;
  if (minutes > 60 && minutes <= 1440) return 48;
  return 48;
};

export const calculateRanges = (preloadRange: RangeValue) => {
  const totalMinutes = preloadRange?.[1]?.diff(preloadRange?.[0], 'minutes', true) || 1;
  const totalMillis = preloadRange?.[1]?.diff(preloadRange?.[0], 'milliseconds', true) || 1;
  const totalFrames = calcMaxFramesToPreload(totalMinutes);
  const totalFramesToPreload = totalFrames;
  const samplingDuration = totalMillis / totalFrames;
  const lastDateToPreload = preloadRange?.[1];
  return {
    totalMinutes, totalFrames, totalFramesToPreload, samplingDuration, lastDateToPreload,
  };
};

export const generateTimestampList = (
  rangeValue: RangeValue,
  interval: number,
): string[] => {
  if (interval <= 0) return [];

  let currentTimestamp: Dayjs = rangeValue[0];
  const endTimestamp : Dayjs = rangeValue[1];
  const timestamps: string[] = [];

  const totalMinutes = endTimestamp.diff(rangeValue[0], 'minutes', true);
  const expectedFrames = calcMaxFramesToPreload(totalMinutes);

  for (let i = 0; i < expectedFrames; i += 1) {
    try {
      timestamps.push(currentTimestamp.toISOString());
      currentTimestamp = currentTimestamp.add(Math.max(1, Math.round(interval)), 'millisecond');
    } catch (e) {
      logger.error('Error generating timestamp list', e);
      break;
    }
  }

  return timestamps;
};

export const findNearestPlaneObservationByTimestamp = (
  data: PlaneObservation[],
  targetTimestamp: Dayjs,
): PlaneObservation | null => {
  if (data.length === 0) return null;

  const targetTimestampMillis = targetTimestamp.valueOf();
  const lowerBound = targetTimestampMillis - 11000;
  const upperBound = targetTimestampMillis + 11000;

  const withinRange = data.filter((observation) => {
    const observationTimestampMillis = dayjs(observation.datetime).valueOf();
    return observationTimestampMillis >= lowerBound && observationTimestampMillis <= upperBound;
  });

  if (withinRange.length === 0) return null;

  const nearestPlaneObservation = withinRange.reduce((closest, current) => {
    const currentDiff = Math.abs(dayjs(current.datetime).diff(targetTimestamp));
    const closestDiff = Math.abs(dayjs(closest.datetime).diff(targetTimestamp));

    return currentDiff < closestDiff ? current : closest;
  });

  return nearestPlaneObservation;
};

export const findClosestIsoDate = (
  dateObj: Record<string, unknown[]>,
  targetIsoDate: string,
): string => {
  const targetDate = dayjs(targetIsoDate);

  let closestDate = '';
  let smallestDiff = 10 * 60 * 1000;

  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const dateIsoString in dateObj) {
    const currentDate = dayjs(dateIsoString);
    const diff = Math.abs(currentDate.diff(targetDate));

    if (diff < smallestDiff) {
      smallestDiff = diff;
      closestDate = dateIsoString;
    }
  }

  return closestDate;
};

export const filterPlaneDataByAirportName = (planes: ADSBPositions[]): ADSBPositions[] => {
  const mainAirport = process.env.REACT_APP_MAIN_AIRPORT_NAME;
  return planes.filter(
    (plane) => plane.origin === mainAirport || plane.destination === mainAirport,
  );
};

export const parseEnvSupervisorProduct = (envVar?: string) => {
  // Comprobamos si la variable de entorno existe y es un string
  if (!envVar || typeof envVar !== 'string') {
    logger.error('The environment variable for supervisor products does not exist or is not a string');
    return null;
  }

  // Parseamos la variable de entorno separada por comas
  const parsedArray = envVar.split(',');

  // Comprobamos si el array tiene exactamente 4 elementos
  if (parsedArray.length !== 4) {
    logger.error('The environment variable for supervisor products does not have exactly 4 elements');
    return null;
  }

  // Devolvemos el array parseado
  return parsedArray;
};
